/**
 * Camada 1 · a `FonteAjuda` que fala com o `services/knowledge` por HTTP.
 *
 * MORA AQUI E NAO EM CADA APP de proposito. Escrita no app, ela nasceria duas vezes, e as
 * duas divergiriam na primeira mudanca de rota: a que envelhece e a do produto que ninguem
 * abriu naquela semana. O `guarda-fronteira.sh` recusa `fetch` fora de `src/dados/` nos
 * apps exatamente para empurrar esta decisao para ca.
 *
 * ELA NAO CONHECE O NAMESPACE. Quem o conhece e o produto, pelo `product.config`, e ele
 * chega em toda chamada porque o contrato o exige. Guardar o namespace dentro da fonte
 * pareceria mais comodo e criaria o defeito que `packages/help` existe para evitar: um
 * padrao silencioso que faz dois produtos verem a ajuda um do outro.
 */

import { idiomaAtual, type Idioma } from '@complexity/i18n';
import type { ArtigoAjuda, FonteAjuda } from './index.js';

/**
 * Constroi a fonte a partir da base da API.
 *
 * BASE VAZIA DEVOLVE UMA FONTE QUE FALHA, e nao `null`: o painel ja sabe transformar falha
 * no estado `indisponivel`, com o motivo na tela. Devolver `null` obrigaria cada app a
 * decidir o que fazer, e o que eles fariam e esconder o botao, que e pior: a pessoa nao
 * saberia que a ajuda existe.
 */
export interface CredenciaisAjuda {
  /**
   * De onde sai o token e o tenant da chamada.
   *
   * FUNCAO E NAO VALOR, e isso importa: o token EXPIRA e e renovado, e um valor capturado
   * na montagem envelheceria dentro do componente sem ninguem perceber. A funcao le o
   * estado atual a cada chamada.
   */
  readonly obter: () => { readonly token?: string; readonly tenantId?: string };
}

export function criarFonteAjudaHttp(
  base: string | undefined,
  credenciais?: CredenciaisAjuda,
): FonteAjuda {
  const raiz = (base ?? '').replace(/\/+$/, '');

  /** Os cabecalhos da chamada, montados a cada uma para o token nunca estar velho. */
  function cabecalhos(): Record<string, string> {
    const c: Record<string, string> = { Accept: 'application/json' };
    const atual = credenciais?.obter();
    if (atual?.token) c.Authorization = `Bearer ${atual.token}`;
    // O tenant e OMITIDO quando ausente, e nao mandado vazio: string vazia chega ao
    // servidor como tenant presente e falso, que e pior que tenant ausente.
    if (atual?.tenantId) c['X-Tenant-Id'] = atual.tenantId;
    return c;
  }

  /**
   * O idioma da chamada, como parametro de consulta (ADR 0060).
   *
   * NA QUERY, E NAO NO CAMINHO: os padroes pre-tenant do ADR 0044 tem contagem de segmentos
   * FIXA, e um segmento a mais tiraria a leitura da isencao. O interceptor compara
   * `requisicao.path`, que nao inclui a query, entao `?idioma=` nao muda nada la.
   *
   * AUSENTE, VALE O IDIOMA DA TELA NO INSTANTE DA CHAMADA, lido aqui e nao na criacao da
   * fonte: a fonte e memorizada pelo chassi, e um idioma capturado na criacao congelaria a
   * primeira escolha da pessoa.
   */
  function sufixoDeIdioma(idioma: Idioma | undefined, separador: '?' | '&'): string {
    return `${separador}idioma=${encodeURIComponent(idioma ?? idiomaAtual())}`;
  }

  async function pedir<T>(caminho: string): Promise<T> {
    if (raiz === '') {
      throw new Error('A base da API de ajuda não está configurada neste ambiente.');
    }
    const resposta = await fetch(`${raiz}${caminho}`, {
      // SEM `credentials`, e isso NAO e descuido: a sessao viaja no cabecalho
      // `Authorization`, e nao em cookie. Com 'include', o navegador exige
      // `Access-Control-Allow-Credentials` no preflight, o servico nao o manda (por
      // decisao, contra CSRF), e a chamada e RECUSADA antes de sair.
      //
      // O `curl` passava porque nao faz preflight, e por isso o defeito parecia de rede.
      headers: cabecalhos(),
    });
    if (!resposta.ok) {
      // O NUMERO VAI PARA O LOG, e nunca para a tela, pela regra dura de 07/09/2026. Quem
      // resolve precisa dele; quem opera nao tem o que fazer com um 401 na tela.
      console.warn(`[ajuda] ${caminho} respondeu ${resposta.status}.`);

      // A FRASE DA TELA SAI DO CORPO, que o servico ja manda em portugues e ja distingue os
      // casos: "Token de acesso ausente." e "Sessao invalida ou expirada." tem o MESMO 401,
      // e sao problemas diferentes. O corpo entrega mais causa que o codigo, e nao menos.
      const dito = await resposta
        .clone()
        .json()
        .then((c: unknown) =>
          typeof c === 'object' && c !== null && typeof (c as { message?: unknown }).message === 'string'
            ? ((c as { message: string }).message)
            : '',
        )
        .catch(() => '');

      throw new Error(dito !== '' ? dito : 'Não foi possível falar com o serviço agora. Tente de novo em instantes.');
    }
    return (await resposta.json()) as T;
  }

  return {
    async buscar(namespace: string, termo: string, idioma?: Idioma): Promise<readonly ArtigoAjuda[]> {
      // SEM TOKEN, NAO SE CHAMA: a policy da 0010 devolveria so os artigos da plataforma,
      // e uma chamada prematura toma 401 e deixa "a ajuda nao respondeu" na tela.
      if (!credenciais?.obter().token) return [];
      // `encodeURIComponent` nos dois: o termo vem do que a pessoa digitou, e um `&` ou `#`
      // ali cortaria a busca ao meio sem erro nenhum aparecer.
      const ns = encodeURIComponent(namespace);
      const t = encodeURIComponent(termo);
      return pedir<readonly ArtigoAjuda[]>(`/artigos/${ns}?termo=${t}${sufixoDeIdioma(idioma, '&')}`);
    },

    async obter(namespace: string, id: string, idioma?: Idioma): Promise<string | null> {
      const ns = encodeURIComponent(namespace);
      const artigo = encodeURIComponent(id);
      try {
        // `corpo_md` e o nome que a rota devolve, medido em
        // services/knowledge/src/artigos/artigos.controller.ts:54. Escrever `corpo` aqui
        // produziria `undefined` silencioso: a chamada daria certo, o painel abriria, e o
        // artigo apareceria em branco sem erro nenhum.
        const r = await pedir<{ readonly corpo_md: string }>(
          `/artigos/${ns}/${artigo}${sufixoDeIdioma(idioma, '?')}`,
        );
        return r.corpo_md;
      } catch {
        // O contrato manda devolver `null` quando nao acha, e a rota devolve 404 tanto para
        // artigo inexistente quanto para artigo de outro cliente. A indistincao e proposital
        // no servico, e mante-la aqui e o que impede a tela de contar a diferenca.
        return null;
      }
    },
  };
}
